import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  Avatar,
  ListItem,
  useTheme,
  Chip,
  Badge
} from '@mui/material';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocalHospital as LocalHospitalIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  Notifications as NotificationsIcon,
  BugReport as BugReportIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  AutoFixHigh as AutoFixHighIcon,
  ExpandLess, ExpandMore,
  Pets as PetsIcon,
  Inventory as InventoryIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Medication as MedicationIcon,
  Description as DocumentIcon,
  Message as MessageIcon,
  AccountCircle as ProfileIcon,
  MonitorHeart as MonitorHeartIcon,
  Queue as QueueIcon,
  Description as DescriptionIcon,
  VpnKey as VpnKeyIcon,
  Webhook as WebhookIcon,
  Flag as FlagIcon,
  Build as BuildIcon,
  VideoCall as VideoCallIcon,
  Receipt as ReceiptIcon,
  MeetingRoom as ReceptionIcon,
  Favorite as AdoptionIcon,
  Home as ShelterIcon,
  Shield as PreventionIcon,
  Science as LabsIcon,
  Campaign as CampaignIcon,
  LocalHospital as ClinicsIcon,
  Upload as UploadIcon,
  CalendarToday as CalendarIcon,
  Healing as StethoscopeIcon,
  FavoriteBorder as HomeHeartIcon,
  Lightbulb as LightbulbIcon,
  Inbox as InboxIcon,
  Description as FileTextIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasFeature, MENU_FEATURES } from '../utils/features';
import { useMenuItems } from '../hooks/useMenuItems';
import { useNotifications } from '../contexts/NotificationContext';

const DRAWER_WIDTH = 260;

// Mapper pentru iconuri - transforma string-uri in componente React
const getIconComponent = (iconName: string | null): React.ReactNode => {
  if (!iconName) return <DashboardIcon />;
  
  const iconMap: { [key: string]: React.ReactNode } = {
    Dashboard: <DashboardIcon />,
    Pets: <PetsIcon />,
    CalendarToday: <CalendarIcon />,
    Healing: <StethoscopeIcon />,
    FavoriteBorder: <HomeHeartIcon />,
    Lightbulb: <LightbulbIcon />,
    Inbox: <InboxIcon />,
    Notifications: <NotificationsIcon />,
    Description: <FileTextIcon />,
    Receipt: <ReceiptIcon />,
    Campaign: <CampaignIcon />,
    Star: <StarIcon />,
    Settings: <SettingsIcon />,
    Event: <EventIcon />,
    Assignment: <AssignmentIcon />,
    Message: <MessageIcon />,
    Medication: <MedicationIcon />,
    LocalHospital: <LocalHospitalIcon />,
    VideoCall: <VideoCallIcon />,
    Assessment: <AssessmentIcon />,
    Upload: <UploadIcon />,
    People: <PeopleIcon />,
    Science: <LabsIcon />,
    MeetingRoom: <ReceptionIcon />,
    Payment: <PaymentIcon />,
  };
  
  return iconMap[iconName] || <DashboardIcon />;
};

// Stilizare pentru item-uri active
const activeSx = {
  bgcolor: 'action.selected',
  borderLeft: '3px solid',
  borderColor: 'primary.main',
  '& .MuiListItemIcon-root': {
    color: 'primary.main',
  },
  '& .MuiListItemText-primary': {
    color: 'primary.main',
    fontWeight: 500,
  },
};

// Un list item reutilizabil cu highlight pentru ruta activa
const Item = ({
  to, icon, label, onClick, isDark, badge, highlight
}: { 
  to: string; 
  icon: React.ReactNode; 
  label: string; 
  onClick?: () => void; 
  isDark?: boolean;
  badge?: number;
  highlight?: boolean;
}) => {
  const activeDarkSx = isDark ? {
    bgcolor: 'rgba(255,255,255,0.08)',
    borderLeft: '3px solid',
    borderColor: 'primary.main',
    '& .MuiListItemIcon-root': {
      color: '#90caf9',
    },
    '& .MuiListItemText-primary': {
      color: '#90caf9',
      fontWeight: 500,
    },
  } : activeSx;

  return (
    <ListItemButton
      component={NavLink}
      to={to}
      end
      onClick={onClick}
      aria-label={label}
      sx={{
        borderRadius: 1,
        mx: 1,
        mb: 0.25, // Redus pentru spacing mai compact
        py: 0.75, // Padding vertical redus pentru compactitate
        '&.active': activeDarkSx,
        '&:hover': {
          bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'action.hover',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: isDark ? '#bdbdbd' : 'inherit' }}>
        {badge !== undefined && badge > 0 ? (
          <Badge badgeContent={badge} color="error" max={99}>
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </ListItemIcon>
      <ListItemText 
        primary={label} 
        primaryTypographyProps={{ 
          fontWeight: highlight ? 600 : 500,
          fontSize: '0.8125rem',
          color: highlight ? '#3B82F6' : undefined
        }}
      />
    </ListItemButton>
  );
};

// Componenta pentru renderizarea meniurilor dinamice pentru clinic_admin cu submeniuri expandabile
const ClinicAdminMenuItems = ({ 
  menuItems, 
  isDark, 
  navigate,
  hasFullAccess,
  userPlanFeatures,
  location
}: { 
  menuItems: Array<{
    id: number;
    key: string;
    label: string;
    route: string;
    icon: string | null;
    order: number;
    section: string | null;
    is_enabled: boolean;
    is_visible: boolean;
    badge_config: any;
    metadata: any;
  }>; 
  isDark: boolean;
  navigate: (path: string) => void;
  hasFullAccess: boolean;
  userPlanFeatures?: string[];
  location: any;
}) => {
  const [mgmtOpen, setMgmtOpen] = useState(false);
  const [finOpen, setFinOpen] = useState(false);
  const [sysOpen, setSysOpen] = useState(false);

  // Grupeaza meniurile
  const groups: { [key: string]: typeof menuItems } = {};
  const standaloneItems: typeof menuItems = [];

  menuItems.forEach(item => {
    if (!item.is_enabled || !item.is_visible) return;
    
    const metadata = item.metadata ? (typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata) : null;
    
    // Verifica daca are parent (e submeniu)
    if (metadata?.parent) {
      const parentKey = metadata.parent;
      if (!groups[parentKey]) groups[parentKey] = [];
      groups[parentKey].push(item);
    } else if (metadata?.group) {
      // E grup header (nu il afisam separat, folosim doar pentru grupare)
      if (!groups[item.key]) groups[item.key] = [];
    } else {
      // E item standalone
      standaloneItems.push(item);
    }
  });

  // Sorteaza item-urile standalone
  standaloneItems.sort((a, b) => a.order - b.order);

  // Auto-expand groups based on current route
  useEffect(() => {
    if (/\/clinic\/(patients|appointments|doctors|clients)/.test(location.pathname)) {
      setMgmtOpen(true);
    }
    if (/\/clinic\/(services|prescriptions|medications|billing)/.test(location.pathname)) {
      setFinOpen(true);
    }
    if (/\/clinic\/(notifications|profile|settings)/.test(location.pathname)) {
      setSysOpen(true);
    }
  }, [location.pathname]);

  // Helper pentru a verifica daca un item trebuie afisat (feature flags)
  const shouldShowItem = (item: typeof menuItems[0]): boolean => {
    const metadata = item.metadata ? (typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata) : null;
    const requiresFeature = metadata?.requiresFeature;
    
    if (!requiresFeature) return true;
    if (hasFullAccess) return true;
    
    return hasFeature(requiresFeature as any, userPlanFeatures);
  };

  // Helper pentru a obtine iconul grupului
  const getGroupIcon = (groupKey: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'clinic_admin.management': <PeopleIcon />,
      'clinic_admin.financial': <PaymentIcon />,
      'clinic_admin.system': <SettingsIcon />,
    };
    return iconMap[groupKey] || <DashboardIcon />;
  };

  return (
    <>
      {/* Items standalone (Dashboard) */}
      {standaloneItems
        .filter(shouldShowItem)
        .map((item) => (
          <Item
            key={item.id}
            isDark={isDark}
            to={item.route}
            icon={getIconComponent(item.icon)}
            label={item.label}
          />
        ))}

      {/* Management Group */}
      {groups['clinic_admin.management'] && groups['clinic_admin.management'].length > 0 && (
        <>
          <ListItemButton 
            onClick={() => setMgmtOpen(v => !v)} 
            aria-label="Deschide Management"
            sx={{ 
              mx: 1, 
              my: 0.5,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getGroupIcon('clinic_admin.management')}
            </ListItemIcon>
            <ListItemText primary="Management" />
            {mgmtOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={mgmtOpen} unmountOnExit>
            <List disablePadding sx={{ pl: 3 }}>
              {groups['clinic_admin.management']
                .sort((a, b) => a.order - b.order)
                .filter(shouldShowItem)
                .map((item) => (
                  <Item
                    key={item.id}
                    isDark={isDark}
                    to={item.route}
                    icon={getIconComponent(item.icon)}
                    label={item.label}
                  />
                ))}
            </List>
          </Collapse>
        </>
      )}

      {/* Financial Group */}
      {groups['clinic_admin.financial'] && groups['clinic_admin.financial'].length > 0 && (
        <>
          <ListItemButton 
            onClick={() => setFinOpen(v => !v)} 
            aria-label="Deschide Servicii & Financiar"
            sx={{ 
              mx: 1, 
              my: 0.5,
              borderRadius: 1,
    '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getGroupIcon('clinic_admin.financial')}
            </ListItemIcon>
            <ListItemText primary="Servicii & Financiar" />
            {finOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={finOpen} unmountOnExit>
            <List disablePadding sx={{ pl: 3 }}>
              {groups['clinic_admin.financial']
                .sort((a, b) => a.order - b.order)
                .filter(shouldShowItem)
                .map((item) => (
                  <Item
                    key={item.id}
                    isDark={isDark}
                    to={item.route}
                    icon={getIconComponent(item.icon)}
                    label={item.label}
                  />
                ))}
            </List>
          </Collapse>
        </>
      )}

      {/* System Group */}
      {groups['clinic_admin.system'] && groups['clinic_admin.system'].length > 0 && (
        <>
          <ListItemButton 
            onClick={() => setSysOpen(v => !v)} 
            aria-label="Deschide Sistem & Setari"
            sx={{ 
              mx: 1, 
              my: 0.5,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getGroupIcon('clinic_admin.system')}
            </ListItemIcon>
            <ListItemText primary="Sistem & Setari" />
            {sysOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={sysOpen} unmountOnExit>
            <List disablePadding sx={{ pl: 3 }}>
              {groups['clinic_admin.system']
                .sort((a, b) => a.order - b.order)
                .filter(shouldShowItem)
                .map((item) => (
                  <Item
                    key={item.id}
                    isDark={isDark}
                    to={item.route}
                    icon={getIconComponent(item.icon)}
                    label={item.label}
                  />
                ))}
            </List>
          </Collapse>
        </>
      )}
    </>
  );
};

// Componenta pentru renderizarea meniurilor dinamice pentru roluri simple (client, shelter_admin, shelter_staff, doctor, veterinarian, reception)
const DynamicMenuItems = ({ 
  menuItems, 
  isDark, 
  onBadgeCount,
  navigate
}: { 
  menuItems: Array<{
    id: number;
    key: string;
    label: string;
    route: string;
    icon: string | null;
    order: number;
    section: string | null;
    is_enabled: boolean;
    is_visible: boolean;
    badge_config: any;
    metadata: any;
  }>; 
  isDark: boolean;
  onBadgeCount?: () => number | undefined;
  navigate: (path: string) => void;
}) => {
  const menuBySection = menuItems.reduce((acc: { [key: string]: typeof menuItems }, item) => {
    const section = item.section || 'OTHER';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <>
      {Object.keys(menuBySection)
        .sort((a, b) => {
          // Ordinea secțiunilor: FUNCTIONAL primul, apoi celelalte alfabetic
          const order = ['FUNCTIONAL', 'MANAGEMENT', 'COMUNITATE', 'CONT', 'ADMIN', 'MEDICAL', 'COMUNICARE'];
          const aIndex = order.indexOf(a);
          const bIndex = order.indexOf(b);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.localeCompare(b);
        })
        .map((section, sectionIndex) => {
        const items = menuBySection[section];
        const sortedItems = [...items].sort((a, b) => a.order - b.order);
        
        return (
          <React.Fragment key={section}>
            {sectionIndex > 0 && <Divider sx={{ my: 1, mx: 2, opacity: 0.3 }} />}
            {sortedItems
              .filter(item => item.is_enabled && item.is_visible)
              .map((item) => {
                const metadata = item.metadata ? (typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata) : null;
                const isHighlighted = metadata?.highlight;
                const badgeText = metadata?.badge;
                
                // Calculam badge count
                let badgeCount: number | undefined = undefined;
                if (item.badge_config) {
                  const badgeConfig = typeof item.badge_config === 'string' ? JSON.parse(item.badge_config) : item.badge_config;
                  if (badgeConfig?.type === 'count' && badgeConfig?.source === 'notifications') {
                    badgeCount = onBadgeCount ? onBadgeCount() : undefined;
                  }
                }
                
                // Special handling pentru Healthypaws+
                if (item.key === 'client.subscriptions' && isHighlighted) {
  return (
    <ListItemButton
                      key={item.id}
      component={NavLink}
                      to={item.route}
      end
                      onClick={() => navigate(item.route)}
                      aria-label={item.label}
      sx={{
        borderRadius: 1,
        mx: 1,
        mb: 0.1,
        py: 0.5,
                        '&.active': isDark ? {
                          bgcolor: 'rgba(255,255,255,0.08)',
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                        } : {
                          bgcolor: 'action.selected',
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                        },
                        '& .MuiListItemIcon-root': {
                          color: '#3B82F6',
                        },
                        '& .MuiListItemText-primary': {
                          color: '#3B82F6',
                          fontWeight: 600,
                        },
        '&:hover': {
                          bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
          '& .MuiListItemIcon-root': {
                            color: '#2563EB',
          },
          '& .MuiListItemText-primary': {
                            color: '#2563EB',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getIconComponent(item.icon)}
      </ListItemIcon>
      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{item.label}</span>
                            {badgeText && (
                              <Chip 
                                label={badgeText} 
                                size="small" 
                                sx={{ 
                                  height: 16,
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  bgcolor: '#3B82F6',
                                  color: '#fff',
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                  }
                                }} 
                              />
                            )}
                          </Box>
                        } 
        primaryTypographyProps={{ 
                          fontWeight: 600,
          fontSize: '0.8125rem'
        }}
      />
    </ListItemButton>
                  );
                }
                
                // Item normal
                return (
                  <Item
                    key={item.id}
                    isDark={isDark}
                    to={item.route}
                    icon={getIconComponent(item.icon)}
                    label={item.label}
                    badge={badgeCount}
                    highlight={isHighlighted}
                  />
                );
              })}
          </React.Fragment>
        );
      })}
      
    </>
  );
};

interface SidebarProps {
  userInitials?: string;
  onSignOut: () => Promise<void> | void;
  onToggleTheme?: () => void;
}

export default function Sidebar({ userInitials = 'U', onSignOut, onToggleTheme }: SidebarProps) {
  const [mgmtOpen, setMgmtOpen] = useState(true);
  const [finOpen, setFinOpen] = useState(true);
  const [sysOpen, setSysOpen] = useState(true);
  const [plansOpen, setPlansOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Incarca meniurile din API pentru toate rolurile (exceptand master_admin)
  const rolesToLoadFromAPI = ['client', 'clinic_admin', 'shelter_admin', 'shelter_staff', 'doctor', 'veterinarian', 'reception'];
  const shouldLoadFromAPI = user?.role && rolesToLoadFromAPI.includes(user.role);
  const { menuItems: dynamicMenuItems, loading: menuLoading } = useMenuItems(shouldLoadFromAPI ? user.role : undefined);
  
  // Pentru clinic_admin folosim hook-ul separat (pentru componenta ClinicAdminMenuItems)
  const { menuItems: clinicMenuItems, loading: clinicMenuLoading } = useMenuItems(user?.role === 'clinic_admin' ? 'clinic_admin' : undefined);
  
  // Badge count pentru notificari (doar pentru clienti) – din API când e client
  const notifications = useNotifications();
  const unreadNotificationsCount = user?.role === 'client' ? (notifications.unreadCount ?? 0) : 0;

  useEffect(() => {
    if (user?.role === 'client' && notifications.refreshUnreadCountFromApi) {
      notifications.refreshUnreadCountFromApi();
    }
  }, [user?.role, user?.id]);
  
  // Verifica daca utilizatorul are acces full (email special sau fara restrictii)
  const hasFullAccess = (
    user?.email === 'doc1@healthypaws.ro' ||
    !user?.planFeatures ||
    (Array.isArray(user?.planFeatures) && user!.planFeatures.length === 0)
  );

  // Auto-expand groups based on current route (doar la mount initial)
  useEffect(() => {
    if (user?.role === 'master_admin') {
      if (/\/master-admin\/(users|clinics|vets|appointments)/.test(location.pathname)) {
        setMgmtOpen(true);
      }
      if (/\/master-admin\/(payments|payment-settings|subscriptions)/.test(location.pathname)) {
        setFinOpen(true);
      }
      if (/\/master-admin\/(plans|notifications|errors|settings)/.test(location.pathname)) {
        setSysOpen(true);
      }
      if (/\/master-admin\/plans\/(clinics|users)/.test(location.pathname)) {
        setPlansOpen(true);
      }
    } else if (user?.role === 'clinic_admin') {
      if (/\/clinic\/(patients|appointments|doctors|clients)/.test(location.pathname)) {
        setMgmtOpen(true);
      }
      if (/\/clinic\/(services|prescriptions|medications|billing|subscriptions|purchase-orders)/.test(location.pathname)) {
        setFinOpen(true);
      }
      if (/\/clinic\/(notifications|profile|settings)/.test(location.pathname)) {
        setSysOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Doar la mount, nu la fiecare schimbare de ruta

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          // Asigura-te ca Drawer-ul este deasupra oricarui continut si primeste click-uri
          zIndex: (theme) => theme.zIndex.drawer + 2,
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
        },
      }}
    >
      {/* Top brand bar - clickable pentru navigare catre dashboard */}
      <Toolbar 
        sx={{ 
          px: 2, 
          gap: 1, 
          minHeight: 64,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          transition: 'background-color 0.2s ease'
        }}
        onClick={() => navigate('/')}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>H</Avatar>
        <Typography variant="h6" fontWeight={700} color="primary">
          HealthyPaws
        </Typography>
      </Toolbar>

      <Divider />

      {/* Continut scrollabil */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <List sx={{ px: 1 }}>
          {/* Dashboard - conditional based on role (doar pentru master_admin care nu folosește DynamicMenuItems) */}
          {user?.role === 'master_admin' && (
            <Item isDark={isDark} to="/master-admin" icon={<DashboardIcon />} label="Dashboard" />
          )}

          {/* Parinte expandabil – MANAGEMENT */}
          {user?.role === 'master_admin' && (
            <>
              <ListItemButton 
                onClick={() => setMgmtOpen(v => !v)} 
                aria-label="Deschide Management"
                sx={{ 
                  mx: 1, 
                  my: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary="Management" />
                {mgmtOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={mgmtOpen} unmountOnExit>
                <List disablePadding sx={{ pl: 3 }}>
                  {/* Utilizatori cu sub-categorii */}
                  <ListItemButton 
                    onClick={() => setUsersOpen(v => !v)} 
                    aria-label="Deschide Utilizatori"
                    sx={{ 
                      mx: 0, 
                      my: 0.5,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Utilizatori" />
                    {usersOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={usersOpen} unmountOnExit>
                    <List disablePadding sx={{ pl: 3 }}>
                      <Item isDark={isDark} to="/master-admin/users/medical" icon={<LocalHospitalIcon />} label="Personal Medical" />
                      <Item isDark={isDark} to="/master-admin/users/clients" icon={<PersonIcon />} label="Clienti" />
                    </List>
                  </Collapse>
                  
                  <Item isDark={isDark} to="/master-admin/clinics" icon={<BusinessIcon />} label="Clinici" />
                  <Item isDark={isDark} to="/master-admin/shelters" icon={<ShelterIcon />} label="Adaposturi" />
                  <Item isDark={isDark} to="/master-admin/vets" icon={<LocalHospitalIcon />} label="Veterinari" />
                  <Item isDark={isDark} to="/master-admin/appointments" icon={<EventIcon />} label="Programari" />
                </List>
              </Collapse>
            </>
          )}

          {/* Meniu pentru clinic_admin - Dinamic din API cu submeniuri expandabile */}
          {user?.role === 'clinic_admin' && (
            <>
              {clinicMenuLoading ? (
                <Item isDark={isDark} to="/clinic" icon={<DashboardIcon />} label="Dashboard" />
              ) : (
                <ClinicAdminMenuItems 
                  menuItems={clinicMenuItems} 
                  isDark={isDark}
                  navigate={navigate}
                  hasFullAccess={hasFullAccess}
                  userPlanFeatures={user?.planFeatures}
                  location={location}
                />
              )}
            </>
          )}

          {/* Meniu pentru clienti - Dinamic din API */}
          {user?.role === 'client' && (
            <>
              {menuLoading ? (
                // Loading state - nu afisam nimic sau doar un skeleton
                <Box sx={{ px: 1, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Se încarcă meniurile...
                  </Typography>
                </Box>
              ) : (
                <DynamicMenuItems 
                  menuItems={dynamicMenuItems} 
                  isDark={isDark}
                  onBadgeCount={() => unreadNotificationsCount > 0 ? unreadNotificationsCount : undefined}
                  navigate={navigate}
                />
              )}
            </>
          )}

          {/* Meniu pentru shelter_admin - Dinamic din API */}
          {user?.role === 'shelter_admin' && (
            <>
              {menuLoading ? (
                <Item isDark={isDark} to="/shelter" icon={<DashboardIcon />} label="Dashboard" />
              ) : (
                <DynamicMenuItems 
                  menuItems={dynamicMenuItems} 
                  isDark={isDark}
                  navigate={navigate}
                />
              )}
            </>
          )}

          {/* Meniu pentru shelter_staff - Dinamic din API */}
          {user?.role === 'shelter_staff' && (
            <>
              {menuLoading ? (
                <Item isDark={isDark} to="/shelter" icon={<DashboardIcon />} label="Dashboard" />
              ) : (
                <DynamicMenuItems 
                  menuItems={dynamicMenuItems} 
                  isDark={isDark}
                  navigate={navigate}
                />
              )}
            </>
          )}

          {/* Meniu pentru doctor si veterinari - Dinamic din API */}
          {(user?.role === 'doctor' || user?.role === 'veterinarian') && (
            <>
              {menuLoading ? (
                <Item isDark={isDark} to="/doctor" icon={<DashboardIcon />} label="Dashboard" />
              ) : (
                <DynamicMenuItems 
                  menuItems={dynamicMenuItems} 
                  isDark={isDark}
                  navigate={navigate}
                />
              )}
            </>
          )}

          {/* Meniu pentru reception - Dinamic din API */}
          {user?.role === 'reception' && (
            <>
              {menuLoading ? (
                <Item isDark={isDark} to="/receptie/dashboard" icon={<ReceptionIcon />} label="Dashboard Receptie" />
              ) : (
                <DynamicMenuItems 
                  menuItems={dynamicMenuItems} 
                  isDark={isDark}
                  navigate={navigate}
                />
              )}
            </>
          )}

          {/* FINANCIAR */}
          {user?.role === 'master_admin' && (
            <>
              <ListItemButton 
                onClick={() => setFinOpen(v => !v)} 
                aria-label="Deschide Financiar"
                sx={{ 
                  mx: 1, 
                  my: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PaymentIcon />
                </ListItemIcon>
                <ListItemText primary="Financiar" />
                {finOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={finOpen} unmountOnExit>
                <List disablePadding sx={{ pl: 3 }}>
                  <Item isDark={isDark} to="/master-admin/payments" icon={<PaymentIcon />} label="Plati" />
                  <Item isDark={isDark} to="/master-admin/payment-settings" icon={<PaymentIcon />} label="Setări plăți clinici (Stripe)" />
                  <Item isDark={isDark} to="/master-admin/subscriptions" icon={<CreditCardIcon />} label="Abonamente" />
                </List>
              </Collapse>
            </>
          )}


          {/* SISTEM */}
          {user?.role === 'master_admin' && (
            <>
              <ListItemButton 
                onClick={() => setSysOpen(v => !v)} 
                aria-label="Deschide Sistem"
                sx={{ 
                  mx: 1, 
                  my: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Sistem" />
                {sysOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={sysOpen} unmountOnExit>
                <List disablePadding sx={{ pl: 3 }}>
                  {/* Submeniu Planuri */}
                  <ListItemButton 
                    onClick={() => setPlansOpen(v => !v)} 
                    aria-label="Deschide Planuri"
                    sx={{ 
                      mx: 1, 
                      my: 0.5,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CreditCardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Planuri" />
                    {plansOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={plansOpen} unmountOnExit>
                    <List disablePadding sx={{ pl: 6 }}>
                      <Item isDark={isDark} to="/master-admin/plans/clinics" icon={<BusinessIcon />} label="Planuri Clinici" />
                      <Item isDark={isDark} to="/master-admin/plans/users" icon={<PeopleIcon />} label="Planuri Utilizatori" />
                    </List>
                  </Collapse>
                  
                  <Item isDark={isDark} to="/master-admin/notifications" icon={<NotificationsIcon />} label="Notificari" />
                  <Item isDark={isDark} to="/master-admin/errors" icon={<BugReportIcon />} label="Erori" />
                  <Item isDark={isDark} to="/master-admin/settings" icon={<SettingsIcon />} label="Setari Master" />
                </List>
              </Collapse>
            </>
          )}


          {/* OPERATIUNI & OBSERVABILITATE - doar pentru master_admin */}
          {user?.role === 'master_admin' && (
            <>
              <ListItemButton 
                onClick={() => setOpsOpen(v => !v)} 
                aria-label="Deschide Operatiuni"
                sx={{ 
                  mx: 1, 
                  my: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <MonitorHeartIcon />
                </ListItemIcon>
                <ListItemText primary="Operatiuni & Observabilitate" />
                {opsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={opsOpen} unmountOnExit>
                <List disablePadding sx={{ pl: 3 }}>
                  <Item isDark={isDark} to="/master-admin/ops/health" icon={<MonitorHeartIcon />} label="Health & Uptime" />
                  <Item isDark={isDark} to="/master-admin/ops/queues" icon={<QueueIcon />} label="Joburi & Cozi" />
                  <Item isDark={isDark} to="/master-admin/ops/logs" icon={<DescriptionIcon />} label="Loguri" />
                  <Item isDark={isDark} to="/master-admin/ops/api-keys" icon={<VpnKeyIcon />} label="API Keys" />
                  <Item isDark={isDark} to="/master-admin/ops/webhooks" icon={<WebhookIcon />} label="Webhooks" />
                  <Item isDark={isDark} to="/master-admin/ops/feature-flags" icon={<FlagIcon />} label="Feature Flags" />
                  <Item isDark={isDark} to="/master-admin/ops/maintenance" icon={<BuildIcon />} label="Maintenance Mode" />
                </List>
              </Collapse>
            </>
          )}

          {/* RAPOARTE & AUTOMATIZARI - doar pentru master_admin */}
          {user?.role === 'master_admin' && (
            <>
              <Item isDark={isDark} to="/master-admin/reports" icon={<AssessmentIcon />} label="Rapoarte & Audit" />
              <Item isDark={isDark} to="/master-admin/automation" icon={<AutoFixHighIcon />} label="Automatizari" />
            </>
          )}

        </List>
      </Box>

    </Drawer>
  );
}
